import { supabase } from '@/integrations/supabase/client';

export interface TeamMember {
  role: 'pbm' | 'realisateur' | 'ops';
  roleLabel: string;
  description: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
}

/**
 * Fetches the team members assigned to a customer for the onboarding intro step.
 * Returns PBM, Director (via team_film_directors), and Ops (prod supervisor).
 */
export async function getAssignedTeamForOnboarding(userId: string): Promise<{
  success: boolean;
  data?: TeamMember[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select(`
        director:team_film_directors!users_director_id_fkey(user_id),
        personal_brand_manager:team_personal_brand_managers!users_personal_brand_manager_id_fkey(user_id),
        ops_id
      `)
      .eq('id', userId)
      .single();

    if (error) throw error;

    const userIds: string[] = [];
    if (data.personal_brand_manager?.user_id) userIds.push(data.personal_brand_manager.user_id);
    if (data.director?.user_id) userIds.push(data.director.user_id);
    if (data.ops_id) userIds.push(data.ops_id);

    // Fetch names and avatars in one query
    let userMap: Record<string, { first_name: string | null; last_name: string | null; avatar_url: string | null }> = {};
    if (userIds.length > 0) {
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, first_name, last_name, avatar_url')
        .in('id', userIds);

      if (!usersError && users) {
        userMap = Object.fromEntries(users.map(u => [u.id, u]));
      }
    }

    const members: TeamMember[] = [];

    if (data.personal_brand_manager?.user_id) {
      const u = userMap[data.personal_brand_manager.user_id];
      members.push({
        role: 'pbm',
        roleLabel: 'Personal Brand Manager',
        description: 'Expert(e) des réseaux sociaux, il/elle définit ta stratégie éditoriale et ta diffusion. Il/elle t\'accompagnera sur les sujets stratégiques et performances au fil de la diffusion.',
        firstName: u?.first_name ?? null,
        lastName: u?.last_name ?? null,
        avatarUrl: u?.avatar_url ?? null,
      });
    }

    if (data.director?.user_id) {
      const u = userMap[data.director.user_id];
      members.push({
        role: 'realisateur',
        roleLabel: 'Réalisateur·rice',
        description: 'Il/elle met en scène tes contenus vidéo et s\'assure que chaque tournage soit à la hauteur de ton image.',
        firstName: u?.first_name ?? null,
        lastName: u?.last_name ?? null,
        avatarUrl: u?.avatar_url ?? null,
      });
    }

    if (data.ops_id) {
      const u = userMap[data.ops_id];
      members.push({
        role: 'ops',
        roleLabel: 'Chargé·e de production',
        description: 'Il/elle t\'accompagne au quotidien et supervise la production de tes contenus.',
        firstName: u?.first_name ?? null,
        lastName: u?.last_name ?? null,
        avatarUrl: u?.avatar_url ?? null,
      });
    }

    return { success: true, data: members };
  } catch (err) {
    console.error('[TeamIntroduction] Error fetching team:', err);
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
}
